import React, { useContext } from 'react';
import { Box, Grid, Typography, Snackbar } from "@material-ui/core";
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import MuiAlert from '@material-ui/lab/Alert';
import ChartWrapper from "./ChartWrapper";
import { useDigitalPR } from "../hooks/useDigitalPR";
import { useStyles } from '../styles/index.style';
import SkeletonWrapper from '../../common/SkeletonWrapper';
import { storeContext } from '../../provider/Provider';

const Legend = ({ labels, colors }) => (
   <Grid 
      container 
      justifyContent="center" 
      spacing={1} 
      style={{ width:150, maxWidth: 150 }}
   >
      {labels.map((label, index) => (
         <Grid item xs={6} key={label}> 
            <Box display="flex" alignItems="center">
               <Box
                  sx={{
                     width: 10,
                     height: 10,
                     borderRadius: '50%',
                     backgroundColor: colors[index],
                     marginRight: 1,
                  }}
               />
               <Typography variant="caption" style={{ fontSize: '0.75rem' }}>
                  {label}
               </Typography>
            </Box>
         </Grid>
      ))}
   </Grid>
);

Legend.propTypes = {
   labels: PropTypes.arrayOf(PropTypes.string).isRequired, 
   colors: PropTypes.arrayOf(PropTypes.string).isRequired, 
};

function Alert({ severity, onClose, children }) {
   return <MuiAlert elevation={6} variant="filled" severity={severity} onClose={onClose}>
      {children}
   </MuiAlert>
}

Alert.propTypes = {
   severity: PropTypes.string,
   onClose: PropTypes.func,
   children: PropTypes.node,
};

Alert.defaultProps = {
   severity: 'success',
   onClose: () => {},
   children: null,
};

const Charts = () => {
   const classes = useStyles();
   const { state } = useContext(storeContext);
   const { barOptions, monthBarOptions, distributionOptions, linkSeries, linkAcquisitionSeries, distributionSeries, snackbarOpen, handleCloseSnackbar } = useDigitalPR();

   return (
      <Box className={classes.wrapper}>
         <Box 
            display="flex" 
            flexWrap="wrap"
            style={{ gap: "15px", alignItems: "stretch" }}
         >
            <ChartWrapper title="Links by Campaign Month" titleColor="#58595B" style={{ flex: 1, minWidth: '250px' }} tooltipText="These are the number of Digital PR links you have gained for your campaigns based on your billing date.">
               <SkeletonWrapper condition={state.digitalPR.linksByMonthLoading}>
                  {linkSeries[0].data.length > 0 ? 
                  <Chart
                     options={{
                        ...monthBarOptions,
                        chart: {
                           ...barOptions.chart,
                           fontSize: '10px',
                        },
                        dataLabels: {
                           style: { fontSize: '10px' },
                        },
                        legend: { fontSize: '10px' },
                     }}
                     series={linkSeries}
                     type="bar"
                     height="250"
                  /> :
                  <Box display="flex" height="250px" justifyContent="center" alignItems="center">No Data Available</Box>}
               </SkeletonWrapper>
            </ChartWrapper>
            <ChartWrapper title="Links by Acquisition Date" titleColor="#58595B" style={{ flex: 1, minWidth: '250px' }} tooltipText="Links from outreach are picked up over multiple months.">
               <SkeletonWrapper condition={state.digitalPR.linkAcquisitionsByMonthLoading}>
                  {linkAcquisitionSeries.reduce((a, b) => b.data.length + a.data.length) > 0 ? 
                  <Chart
                     options={{
                        ...barOptions,
                        chart: {
                           ...barOptions.chart,
                           events: { click: undefined, mouseMove: undefined },
                           fontSize: '10px',
                        },
                        dataLabels: {
                           style: { fontSize: '10px' },
                        },
                        legend: { fontSize: '10px' },
                     }}
                     series={linkAcquisitionSeries}
                     type="bar"
                     height="250"
                  /> :
                  <Box display="flex" height="250px" justifyContent="center" alignItems="center">No Data Available</Box>}
               </SkeletonWrapper>
            </ChartWrapper>
            <ChartWrapper title="Distribution of Domain Rating for Links" titleColor="#58595B" style={{ flex: 1, minWidth: '250px' }} tooltipText="This pie chart visualizes how your links are spread across different domain ratings, ranging from 0 to 100. It helps you understand the quality and strength of the domains your campaigns are targeting.">
               <SkeletonWrapper condition={state.digitalPR.domainRatingDistributionLoading}>
                  {distributionSeries.length > 0 ? <Box
                     display="flex"
                     flexDirection="row"
                     justifyContent="center"
                     alignItems="center"
                  >
                     <Legend labels={distributionOptions.labels} colors={distributionOptions.colors} />
                     <Chart
                        options={distributionOptions}
                        series={distributionSeries}
                        type="donut"
                        height="250"
                     />
                  </Box> : 
                  <Box display="flex" minHeight="250px" justifyContent="center" alignItems="center">No Data Available</Box>}
               </SkeletonWrapper>
            </ChartWrapper>
         </Box>
         <Snackbar
            open={snackbarOpen && !state.digitalPR.historicCampaignsLoading && !state.digitalPR.campaignsLoading}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
         >
            <Alert
               onClose={handleCloseSnackbar}
               severity="success"
               variant="filled"
               sx={{ width: '100%' }}
            >
               Month filter applied!
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default Charts;
